import React, { useEffect, useMemo, useRef, useState } from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import Styles from "./styles.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    setAllocationModalStatus,
    setAllocationQrInvest,
    setAllocationHash,
    getProject,
    setAllocationHoneeInvest,
} from "../../store/cabinet/action";

import { useAccount, useBalance, useWriteContract } from "wagmi";
import { parseUnits } from "viem";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const erc20Abi = [
    {
        type: "function",
        name: "transfer",
        stateMutability: "nonpayable",
        inputs: [
            { name: "to", type: "address" },
            { name: "value", type: "uint256" },
        ],
        outputs: [{ name: "", type: "bool" }],
    },
];

const USDT_ADDRESS = "0x55d398326f99059fF775485246999027B3197955";

// ---- rate-limit retry helpers
const isRateLimitError = (e) => {
    const msg = String(e?.shortMessage || e?.message || e || "").toLowerCase();
    return (
        msg.includes("rate limit") ||
        msg.includes("rate limited") ||
        msg.includes("429")
    );
};
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const withRateLimitRetry = async (fn, retries = 3) => {
    let attempt = 0;
    while (true) {
        try {
            return await fn();
        } catch (e) {
            attempt += 1;
            if (!isRateLimitError(e) || attempt > retries) throw e;
            await sleep(600 * attempt);
        }
    }
};

const LaunchpadAllocationModal = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const userData = useSelector((store) => store.cabinet.userData);
    const allocationModalStatus = useSelector(
        (store) => store.cabinet.allocationModalStatus
    );
    const projectData = useSelector((store) => store.cabinet.project);
    const transactionInfo = useSelector(
        (store) => store.cabinet.allocationQrInvest
    );

    const [amount, setAmount] = useState("");
    const [currentTab, setCurrentTab] = useState("tron");

    const [localTxHash, setLocalTxHash] = useState(null);
    const [isBackendSending, setIsBackendSending] = useState(false);

    const didHandleHashRef = useRef(false);

    const isHonee = Boolean(userData?.user?.is_honee);
    const slug = projectData?.project?.slug || projectData?.slug;

    const maxAllocation = Math.max(
        0,
        Number(projectData?.allocation_total || 0) -
        Number(projectData?.allocation_invested || 0)
    );

    // wallet hooks (must NOT affect honee flow)
    const { address, isConnected } = useAccount();

    const {
        data: usdtBalance,
        isLoading: isBalanceLoading,
        refetch: refetchBalance,
    } = useBalance({
        address,
        token: USDT_ADDRESS,
        enabled: Boolean(!isHonee && isConnected && address),
        watch: false,
    });

    const { writeContractAsync, isPending } = useWriteContract();

    const closeModal = () => {
        setAmount("");
        setLocalTxHash(null);
        didHandleHashRef.current = false;
        setIsBackendSending(false);
        dispatch(setAllocationModalStatus(false));
    };

    const numericAmount = useMemo(() => Number(amount), [amount]);
    const isAmountValid = useMemo(() => {
        return amount !== "" && !Number.isNaN(numericAmount) && numericAmount > 0;
    }, [amount, numericAmount]);

    const usdtNum = useMemo(() => {
        if (isHonee) return 0;
        return usdtBalance ? Number(usdtBalance.formatted) : 0;
    }, [usdtBalance, isHonee]);

    const isInsufficientBalance =
        !isHonee && isAmountValid && numericAmount > usdtNum;

    // disabled rules split: honee must NOT depend on wallet at all
    const isButtonDisabledHonee = !isAmountValid || !slug || isBackendSending;

    const isButtonDisabledNonHonee =
        !isAmountValid ||
        !isConnected ||
        !transactionInfo?.wallet ||
        !slug ||
        isInsufficientBalance ||
        isPending ||
        isBackendSending;

    const isButtonDisabled = isHonee
        ? isButtonDisabledHonee
        : isButtonDisabledNonHonee;

    const formatBalance = (value) => {
        if (!value) return "0";
        const num = Number(value);
        const floored = Math.floor(num * 100) / 100;
        let str = floored.toFixed(2);
        str = str.replace(/\.?0+$/, "");
        return str;
    };

    const renderUsdtBalance = () => {
        if (!isConnected) return <span>{t("Подключите кошелек")}</span>;
        if (isBalanceLoading) return <span>{t("Загрузка...")}</span>;
        if (!usdtBalance) return <span>0</span>;

        const formatted = formatBalance(usdtBalance.formatted);
        return (
            <span>
        {formatted} {usdtBalance.symbol}
      </span>
        );
    };

    const mainBtnHandler = async () => {
        if (isButtonDisabled) return;

        // ✅ Honee flow: no wallet involvement
        if (isHonee) {
            try {
                setIsBackendSending(true);

                const result = await dispatch(
                    setAllocationHoneeInvest(slug, { amount, network: currentTab })
                );

                // если у API есть success=false
                if (result?.success === false) {
                    throw new Error(result?.message || "Request failed");
                }

                window.open("https://honee.technology/index", "_blank", "noopener");
            } catch (e) {
                const msg =
                    e?.response?.data?.message ||
                    e?.message ||
                    t("Ошибка");
                toast.error(msg, { toastId: "honee-invest-error" });
            } finally {
                setIsBackendSending(false);
            }
            return;
        }

        // Non-honee: onchain transfer
        try {
            didHandleHashRef.current = false;

            const value = parseUnits(amount, 18);
            const hash = await withRateLimitRetry(() =>
                writeContractAsync({
                    address: USDT_ADDRESS,
                    abi: erc20Abi,
                    functionName: "transfer",
                    args: [transactionInfo.wallet, value],
                })
            );

            setLocalTxHash(hash);
        } catch (e) {
            toast.error(
                e?.shortMessage || e?.message || t("Ошибка при отправке транзакции"),
                { toastId: "alloc-send-error" }
            );
        }
    };

    // fetch transactionInfo (wallet etc.)
    useEffect(() => {
        dispatch(setAllocationQrInvest());
    }, [dispatch]);

    // refetch balance only for non-honee on open
    useEffect(() => {
        if (isHonee) return;
        if (!allocationModalStatus) return;
        if (!isConnected || !address) return;

        const id = setTimeout(() => refetchBalance?.(), 600);
        return () => clearTimeout(id);
    }, [allocationModalStatus, isConnected, address, refetchBalance, isHonee]);


    // Send txHash + amount to backend only for non-honee
    useEffect(() => {
        if (isHonee) return;
        if (!localTxHash) return;
        if (didHandleHashRef.current) return;

        didHandleHashRef.current = true;

        const sentAmount = amount;
        const txHash = localTxHash;

        (async () => {
            try {
                setIsBackendSending(true);

                const res = await dispatch(
                    setAllocationHash(slug, { amount: sentAmount, hash: txHash })
                );

                if (res?.error) throw new Error(res?.error?.message || "Backend error");

                toast(t("Ваша транзакция успешно отправлена"));

                setAmount("");
                setLocalTxHash(null);
                didHandleHashRef.current = false;
                setIsBackendSending(false);
                dispatch(setAllocationModalStatus(false));
                dispatch(getProject(slug));

                refetchBalance?.();
            } catch (err) {
                setIsBackendSending(false);
                toast.error(`${t("Ошибка")}: ${err?.message || String(err)}`, {
                    toastId: `alloc-backend-fail-${txHash}`,
                });
                didHandleHashRef.current = false;
            }
        })();
    }, [isHonee, localTxHash, amount, dispatch, slug, t, refetchBalance]);

    return (
        <Popup
            open={allocationModalStatus}
            modal
            nested
            closeOnDocumentClick={false}
            closeOnEscape={false}
            onClose={closeModal}
        >
            <div className={Styles.modalContainer}>
                <div className={Styles.modalCloseBtn} onClick={closeModal}>
                    <div />
                    <div />
                </div>

                <div className={Styles.modalContentWrap}>
                    <div className={Styles.modalTitle}>{t("Новая Инвестиция")}</div>

                    <div className={Styles.modalNote}>
            <span>
              {t("Поздравляем, вам выделена аллокация в размере")}:{" "}
                {projectData?.allocation_total ? projectData.allocation_total * 1 : 0}$
            </span>
                    </div>

                    {isHonee ? (
                        <div className={Styles.tabWrap}>
                            <div className={Styles.tabWrapTitle}>{t("Сеть")}:</div>
                            <div className={Styles.tabRow}>
                                <div
                                    className={`${Styles.tabRowItem} ${
                                        currentTab === "tron" ? Styles.tabRowItemActive : ""
                                    }`}
                                    onClick={() => setCurrentTab("tron")}
                                >
                                    TRON
                                </div>
                                <div
                                    className={`${Styles.tabRowItem} ${
                                        currentTab === "bsc" ? Styles.tabRowItemActive : ""
                                    }`}
                                    onClick={() => setCurrentTab("bsc")}
                                >
                                    BSC
                                </div>
                            </div>
                        </div>
                    ) : null}

                    <div className={Styles.inputWrap}>
                        <div className={Styles.inputTitle}>{t("Введите сумму")}</div>

                        <input
                            type="text"
                            placeholder="0"
                            value={amount}
                            onChange={(e) => {
                                let value = e.target.value;
                                value = value.replace(/[^0-9.]/g, "");
                                const firstDotIndex = value.indexOf(".");
                                if (firstDotIndex !== -1) {
                                    const beforeDot = value.slice(0, firstDotIndex + 1);
                                    const afterDot = value
                                        .slice(firstDotIndex + 1)
                                        .replace(/\./g, "");
                                    value = beforeDot + afterDot;
                                }
                                setAmount(value);
                            }}
                        />

                        {!isHonee && (
                            <div className={Styles.inputNoteWrap}>
                                <div className={Styles.inputNote}>
                                    {`${t("Баланс")} USDT (BSC)`}: {renderUsdtBalance()}
                                </div>

                                <div className={Styles.inputNote}>
                                    {t("Доступно")}: <span>{maxAllocation}$</span> {t("из")}{" "}
                                    {projectData?.allocation_total * 1 || 0}$
                                </div>
                            </div>
                        )}

                        {isHonee && (
                            <div className={`${Styles.inputNoteWrap} ${Styles.inputNoteWrapHonee}`}>
                                <div className={Styles.inputNote}>
                                    {t("Доступно")}: <span>{maxAllocation}$</span> {t("из")}{" "}
                                    {projectData?.allocation_total * 1 || 0}$
                                </div>
                            </div>
                        )}
                    </div>

                    <div
                        className={`${Styles.btnDsb} ${isAmountValid ? Styles.btnActive : ""}`}
                        onClick={isButtonDisabled ? undefined : mainBtnHandler}
                        role="button"
                    >
                        {!isHonee && isInsufficientBalance
                            ? `${t("Недостаточно")} USDT`
                            : !isHonee && isPending
                                ? t("Отправка...")
                                : t("Инвестировать")}
                    </div>
                </div>
            </div>
        </Popup>
    );
};

export default LaunchpadAllocationModal;

